import React, { useContext, FC, useState, useRef, useCallback, SyntheticEvent } from 'react';
import { NavElement, NavMenuElement, NavElementDrop } from './NavElement';
import { MenuListProps, NavMenuVariant, Profile } from './SideNavTypes';
import { NavContext } from './NavContext';
import { ContactUs } from './ContactUs';
import { find, map, get, isEmpty, isEqual } from 'lodash';
import { setRelAttribute } from '@trip-ux/common';

const AuthList: FC<{
  profile: Profile;
  isAuthenticated: boolean;
  userName: string;
}> = ({ profile, isAuthenticated, userName }) => {
  return (
    <>
      {profile && !isAuthenticated && (
        <li className="sign-in" role="none">
          <a href={profile.signInLink} target="_self" role="menuitem">
            {profile.signInLabel}
          </a>
        </li>
      )}
      {profile && isAuthenticated && (
        <li className="profile" role="none">
          <NavElement {...profile.authProfile} label={userName} ariaLabel={userName} />
        </li>
      )}
    </>
  );
};
AuthList.displayName = 'AuthList';

export const MenuList: FC<MenuListProps> = ({
  className,
  id,
  items,
  languageToggleLabel,
  languageToggleAriaLabel,
  languageToggleLink,
  isAuthenticated,
  staticSubMenuNavId,
  profile,
  contactBackButtonLabel,
  contactInfo,
  contactUs,
  onLanguageToggle,
  userName
}) => {
  const context = useContext(NavContext);
  const [langFocusOrLeave, setLangFocusOrLeave] = useState(false);
  const ref = useRef<HTMLAnchorElement | HTMLButtonElement>();
  const contactCallback = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      context.setShowContact(!context.showContact);
      setTimeout(() => ref.current.focus(), 0);
    },
    [context]
  );
  const staticSubMenuList = get(
    find(items, item => isEqual(item.navId, staticSubMenuNavId)),
    'subMenuList'
  );
  const isStaticSubMenu = !context.isMobile && !isEmpty(staticSubMenuList);
  const staticSubMenuClass = isStaticSubMenu ? 'h-100' : '';
  return (
    <div className={`${className} ${staticSubMenuClass}`}>
      {!context.showContact && (
        <div className={`d-flex ${staticSubMenuClass}`}>
          <ul role="menu" id={id} className="position-relative">
            {map(items, (item, idx) => (
              <li key={`${item.navId}-li-${idx}`} role="menuitem" data-testid={`${item.navId}-li-${idx}`}>
                <NavElement {...item} disableDrop={isStaticSubMenu} />
              </li>
            ))}
            {context.isMobile && (
              <>
                <li
                  data-testid="language-toggle-list-item"
                  className={`language-toggle ${langFocusOrLeave ? 'lang-focus' : ''}`}
                  role="none">                  
                  <a
                    data-testid="language-toggle"
                    href={languageToggleLink}
                    target="_self"
                    role="menuitem"
                    onMouseOver={() => setLangFocusOrLeave(true)}
                    onFocus={() => setLangFocusOrLeave(true)}
                    onBlur={() => setLangFocusOrLeave(false)}
                    onMouseLeave={() => setLangFocusOrLeave(false)}
                    onClick={onLanguageToggle}
                    aria-label={languageToggleAriaLabel ? languageToggleAriaLabel : languageToggleLabel}
                    rel={setRelAttribute(languageToggleLink)}>
                    {languageToggleLabel}
                  </a>
                </li>
                <AuthList profile={profile} isAuthenticated={isAuthenticated} userName={userName} />
              </>
            )}
          </ul>
          {isStaticSubMenu && <NavElementDrop subMenuList={staticSubMenuList} isStatic={true} />}
        </div>
      )}     
    </div>
  );
};

MenuList.displayName = 'MenuList';
export default MenuList;
